import { z } from 'zod';
import { Estimate } from './estimate.js';
/**
 * Quy chuẩn công việc, nhìn từ **trong** một dự án — APM-1308b.
 *
 * ## Vì sao form phải tự kiểm được, và vì sao không được viết lại luật
 *
 * `APM-1308a` kiểm ở API và trả cảnh báo trong `warnings[]`. Nhưng một cảnh báo
 * chỉ đến **sau khi bấm lưu** thì người dùng đã gõ xong, đã bấm, và giờ phải
 * quay lại sửa — trong khi thứ họ cần là biết ngay lúc gõ số 2 vào ô ước lượng.
 *
 * Nên luật nằm ở đây, dạng **hàm thuần**, và **cả hai phía gọi nó**:
 * `AssignmentWarningService` cho câu trả lời chính thức, form cho câu trả lời
 * tức thì. Viết lại luật ở client là cách chắc chắn nhất để hai bên lệch nhau
 * đúng vào hôm admin đổi trần — và bên lệch sẽ là bên người dùng nhìn thấy.
 */
export const WorkStandards = z.object({
    /** Ước lượng tối thiểu, giờ — cận dưới 8/80 của PMBOK. */
    taskMinHours: z.number().nonnegative(),
    /** Ước lượng tối đa, giờ — cận trên 8/80. */
    taskMaxHours: z.number().positive(),
    /**
     * Task dài tối đa, tính bằng **ngày lịch** của khoảng `[startDate, dueDate]`.
     *
     * Lấy từ `project.wbs_max_days` chứ không từ quy chuẩn tổ chức: quy chuẩn chỉ
     * là **giá trị mặc định** lúc tạo dự án, còn team Waterfall làm hạ tầng có gói
     * việc dài hơn team web (APM-1308a).
     */
    taskMaxDays: z.number().int().positive(),
    /** Số người tối đa trên một hạng mục — chỉ kiểm được ở server (đếm subtask). */
    taskMaxPeople: z.number().int().positive(),
    /**
     * Số ngày báo trước hạn **đang có hiệu lực** cho dự án này — APM-NOTI-h.
     *
     * Đã giải xong `project.due_soon_days ?? notification.due_soon_days`, nên chỗ
     * gọi không phải tự ghép hai nguồn — hai chỗ tự ghép là hai chỗ sẽ ghép khác
     * nhau. Job quét hạn (`APM-NOTI-i`) đọc đúng con số này.
     */
    dueSoonDays: z.number().int().positive(),
});
/**
 * Những cảnh báo suy ra được **chỉ từ bản nháp** — APM-1308a, APM-1308b.
 *
 * ## Vì sao chỉ có ba, không phải bảy
 *
 * Bốn cảnh báo còn lại (`assignee_overloaded`, `too_many_people`,
 * `wip_limit_exceeded`) cần đọc dữ liệu: lịch của người được giao, subtask của
 * hạng mục, số thẻ trong cột. Form không có chúng và **không nên** có — kéo cả
 * lịch làm việc của một người về client mỗi lần gõ một ký tự là đổi một cảnh
 * báo lấy một vòng mạng. Chúng vẫn tới đủ trong `warnings[]` sau khi lưu.
 *
 * ## Vì sao subtask không bị soi cận dưới
 *
 * Cận 8 giờ áp cho một **gói việc**; subtask được phép nhỏ, đó chính là lý do
 * nó là subtask. Soi cả subtask thì mọi lần chia nhỏ đều sinh ra một tràng cảnh
 * báo về đúng việc vừa được khuyên làm.
 */
export function checkTaskStandards(draft, standards) {
    const warnings = [];
    const { estimateHours, startDate, dueDate } = draft;
    if (estimateHours !== undefined &&
        estimateHours !== null &&
        estimateHours > 0 &&
        !draft.isSubtask) {
        if (estimateHours < standards.taskMinHours) {
            warnings.push({
                code: 'estimate_below_standard',
                message: `Ước lượng ${estimateHours}h dưới mức tối thiểu ${standards.taskMinHours}h. Việc nhỏ thế này thường nên gộp vào hạng mục cha.`,
            });
        }
        else if (estimateHours > standards.taskMaxHours) {
            warnings.push({
                code: 'estimate_above_standard',
                message: `Ước lượng ${estimateHours}h vượt mức tối đa ${standards.taskMaxHours}h. Cân nhắc chia nhỏ — việc lớn thế này thường chỉ lộ ra là trễ vào phút cuối.`,
            });
        }
    }
    if (startDate && dueDate) {
        if (dueDate < startDate) {
            warnings.push({ code: 'due_before_start', message: 'Hạn chót nằm trước ngày bắt đầu.' });
        }
        else {
            const spanDays = daysInclusive(startDate, dueDate);
            if (spanDays > standards.taskMaxDays) {
                warnings.push({
                    code: 'task_exceeds_wbs_limit',
                    message: `Task dài ${spanDays} ngày, vượt trần ${standards.taskMaxDays} ngày của dự án. Cân nhắc chia nhỏ.`,
                });
            }
        }
    }
    return warnings;
}
function daysInclusive(from, to) {
    const ms = new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime();
    return Math.round(ms / 86_400_000) + 1;
}
/**
 * Loại việc, dạng nhẹ cho ô chọn trong form — APM-1308b.
 *
 * Khác `TaskTypeView` của màn hình quản trị: kiểu kia có `slug` và `taskCount`
 * (một phép đếm toàn tổ chức). Một ô chọn trong form không nên là đường để biết
 * tổ chức đang có bao nhiêu việc loại nào.
 */
export const ProjectTaskType = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
});
/**
 * Duyệt **một phần** gợi ý chia nhỏ — APM-1308b, cùng hình dạng với duyệt gợi
 * ý rủi ro.
 *
 * Duyệt trọn gói là cách nhanh nhất để mười subtask được bấm qua mà không ai
 * đọc cái thứ bảy — và cái thứ bảy là cái AI nghĩ ra để đủ danh sách.
 */
export const AcceptBreakdownInput = z.object({
    indexes: z.array(z.number().int().min(0).max(14)).min(1).max(15),
});
/**
 * Hình dạng `ai_suggestion.content` của skill `task_breakdown` — APM-1308b.
 *
 * Trước đây khai trong chính file skill ở `apps/api`. Chuyển về đây khi form có
 * chỗ bấm: giao diện phải đọc được nội dung gợi ý để hiện danh sách subtask cho
 * người duyệt, và một `interface` chép tay ở phía web là nguồn sự thật thứ hai
 * cho cùng một JSON (CLAUDE.md: type dùng chung khai ở `packages/contracts`).
 */
export const BreakdownOutput = z.object({
    subtasks: z
        .array(z.object({
        title: z.string().min(1).max(500),
        estimateHours: z.number().positive().max(200),
        /** Vì sao ước lượng như vậy — spec mục 9 đòi giải thích kèm con số. */
        reason: z.string().max(500),
        /**
         * Hợp đồng ước lượng §0.4 — APM-AI-y. **Tuỳ chọn** có chủ đích.
         *
         * Model chưa gắn dẫn chứng thì subtask vẫn dùng được: `estimateHours` và
         * `reason` là thứ form đã hiện từ APM-1308b, và bắt buộc trường này sẽ
         * làm mọi gợi ý cũ đọc lại thành không hợp lệ. Có nó thì giao diện hiện
         * thêm căn cứ và mức tin cậy **đã bị hệ thống chốt lại**, không phải mức
         * model tự phong.
         */
        estimate: Estimate.optional(),
    }))
        .min(1)
        .max(15),
});
//# sourceMappingURL=work-standards.js.map